/**
 * Encode a single path segment for S3/CDN URLs.
 * Spaces, parentheses (e.g. "file (1).png"), and other reserved chars become %XX.
 * Avoids URL API pathname setter, which can leave `()` unencoded and break staging CDNs.
 */
function encodePathSegment(seg) {
  if (!seg) return seg;
  let decoded = seg;
  try {
    decoded = decodeURIComponent(seg);
  } catch {
    decoded = seg;
  }
  return encodeURIComponent(decoded);
}

function encodePathSegments(relativePath) {
  return String(relativePath)
    .split("/")
    .map(encodePathSegment)
    .join("/");
}

/**
 * Build a safe URL for theme images (S3 keys under REACT_APP_S3_PATH, or full https URLs).
 * Encodes each path segment so spaces, parentheses, %, etc. resolve to the correct object key.
 */
export function getDvSourceImageUrl(imgPath) {
  if (!imgPath) return "";
  const s = String(imgPath).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const origin = u.origin;
      const encodedPath = encodePathSegments(u.pathname.replace(/^\/+/, ""));
      const query = u.search || "";
      const hash = u.hash || "";
      return `${origin}/${encodedPath}${query}${hash}`;
    } catch {
      // encodeURI leaves () alone — encodeURIComponent each segment instead.
      const m = s.match(/^(https?:\/\/[^/]+)(\/.*)?$/i);
      if (!m) return s;
      const pathAndRest = m[2] || "";
      const qIndex = pathAndRest.search(/[?#]/);
      const pathOnly = qIndex >= 0 ? pathAndRest.slice(0, qIndex) : pathAndRest;
      const rest = qIndex >= 0 ? pathAndRest.slice(qIndex) : "";
      return `${m[1]}/${encodePathSegments(pathOnly.replace(/^\/+/, ""))}${rest}`;
    }
  }
  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/?$/, "/");
  const path = s.replace(/^\/+/, "");
  return `${base}${encodePathSegments(path)}`;
}

/**
 * Value for CSS `background-image` / `url(...)`. Unquoted `url(https://.../a(2).png)` is invalid — the `(` ends the URL.
 */
export function toCssUrlValue(href) {
  if (!href) return "none";
  const escaped = String(href).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Pick the mobile vs desktop key. Portrait mobile art must not be used in landscape:
 * many phones stay ≤768px wide when rotated, so width alone would stretch tall
 * portrait assets across a wide viewport.
 */
function pickForViewport(mob, desk) {
  const w = typeof window !== "undefined" ? window.innerWidth : 0;
  const narrow = w > 0 && w <= 768;
  const portrait =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  return (narrow && portrait ? mob || desk : desk || mob) || null;
}

/** `background_mob` vs `background_desk` (login, rules and category pages). */
export function themeBackgroundKeyForViewport(themeData) {
  if (!themeData || typeof themeData !== "object") return null;
  return pickForViewport(themeData.background_mob, themeData.background_desk);
}

/** `game_mob` vs `game_desk` (old/game.php), falling back to the global background. */
export function themeGameBackgroundKeyForViewport(themeData) {
  if (!themeData || typeof themeData !== "object") return null;
  return (
    pickForViewport(themeData.game_mob, themeData.game_desk) ||
    themeBackgroundKeyForViewport(themeData)
  );
}

const NAMED_HEX_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

/** Normalize admin color fields to #rrggbb for color inputs. */
export function normalizeThemeHexColor(val) {
  if (val == null || val === "") return null;
  const v = String(val).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_HEX_COLORS[v.toLowerCase()];
  return named || null;
}
